import { useState, useCallback, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Button,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Badge,
  toast
} from '@etherisc/ui-kit';
import { PlusIcon, TrashIcon } from 'lucide-react';
import { EditableCell } from './EditableCell';
import { AddAdminUnitDialog } from './AddAdminUnitDialog';
import { AdminUnitHierarchyDisplay } from './AdminUnitHierarchyDisplay';


export interface AdminUnitLoadingFactor {
  adminUnitId: string;
  adminUnitName: string;
  adminUnitCode: string;
  adminLevel: number;
  factors: Record<string, number>;
}

export interface AdministrativeUnit {
  id: string;
  countryCode: string;
  adminLevel: number;
  adminName: string;
  adminCode?: string;
  isActive: boolean;
}

interface LoadingFactorsTableProps {
  loadingFactors: AdminUnitLoadingFactor[];
  onChange: (factors: AdminUnitLoadingFactor[]) => void;
  availableAdminUnits: AdministrativeUnit[];
  availablePerils: string[];
  errors?: Array<{
    path: string;
    message: string;
    value: unknown;
  }>;
  readOnly?: boolean;
}

export function LoadingFactorsTable({
  loadingFactors,
  onChange,
  availableAdminUnits,
  availablePerils,
  errors = [],
  readOnly = false
}: LoadingFactorsTableProps) {
  const [showAddDialog, setShowAddDialog] = useState(false);

  const editableCellRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  
  // Get unique perils from the available perils list
  const uniquePerils = [...new Set(availablePerils)];
  
  // Group loading factors by administrative unit for the new table structure
  const adminUnitRows = loadingFactors.map((factor) => ({
    ...factor,
    // Ensure all perils have a value (default to 0 if not set)
    factorsByPeril: uniquePerils.reduce((acc, peril) => {
      acc[peril] = factor.factors[peril] || 0;
      return acc;
    }, {} as Record<string, number>)
  }));

  const handleAddAdminUnit = useCallback((adminUnit: AdministrativeUnit) => {
    // Check if this admin unit already exists
    const existingFactorIndex = loadingFactors.findIndex(f => f.adminUnitId === adminUnit.id);
    
    if (existingFactorIndex >= 0) {
      toast.error(`${adminUnit.adminName} is already in the table`);
      return;
    }

    // Add new admin unit with all perils initialized to 0
    const initialFactors = uniquePerils.reduce((acc, peril) => {
      acc[peril] = 0;
      return acc;
    }, {} as Record<string, number>);

    const newFactor: AdminUnitLoadingFactor = {
      adminUnitId: adminUnit.id, // This should be the UUID from the database
      adminUnitName: adminUnit.adminName,
      adminUnitCode: adminUnit.adminCode || adminUnit.id,
      adminLevel: adminUnit.adminLevel,
      factors: initialFactors
    };
    
    onChange([...loadingFactors, newFactor]);
    toast.success(`Added ${adminUnit.adminName} to loading factors table`);
    setShowAddDialog(false);
  }, [loadingFactors, onChange, availableAdminUnits, uniquePerils]);

  const handleUpdateLoadingFactor = useCallback((adminUnitIndex: number, peril: string, newValue: number) => {
    const updatedFactors = [...loadingFactors];
    updatedFactors[adminUnitIndex] = {
      ...updatedFactors[adminUnitIndex],
      factors: {
        ...updatedFactors[adminUnitIndex].factors,
        [peril]: newValue / 100 // Convert percentage to decimal
      }
    };
    onChange(updatedFactors);
  }, [loadingFactors, onChange]);

  const handleDeleteAdminUnit = useCallback((adminUnitIndex: number) => {
    const updatedFactors = [...loadingFactors];
    const factor = updatedFactors[adminUnitIndex];
    updatedFactors.splice(adminUnitIndex, 1);
    onChange(updatedFactors);
    toast.success(`Removed ${factor.adminUnitName} from loading factors`);
  }, [loadingFactors, onChange]);

  const handleCellNavigation = useCallback((currentRow: number, currentCol: number, direction: 'next' | 'previous') => {
    if (readOnly || uniquePerils.length === 0 || adminUnitRows.length === 0) return;

    let newRow = currentRow;
    let newCol = currentCol;

    if (direction === 'next') {
      newCol++;
      if (newCol >= uniquePerils.length) {
        newCol = 0;
        newRow++;
        if (newRow >= adminUnitRows.length) {
          newRow = 0; // Wrap to first row
        }
      }
    } else {
      newCol--;
      if (newCol < 0) {
        newCol = uniquePerils.length - 1;
        newRow--;
        if (newRow < 0) {
          newRow = adminUnitRows.length - 1; // Wrap to last row
        }
      }
    }


    
    // Focus the new cell
    const cellKey = `${newRow}-${newCol}`;
    const cellElement = editableCellRefs.current[cellKey];
    if (cellElement) {
      // Trigger click to start editing
      cellElement.click();
    }
  }, [readOnly, uniquePerils.length, adminUnitRows.length]);


  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Loading Factors</CardTitle>
            {!readOnly && (
              <Button onClick={() => setShowAddDialog(true)}>
                <PlusIcon size={16} className="mr-2" />
                Add Administrative Unit
              </Button>
            )}
          </div>
          <CardDescription>
            Configure loading factors by administrative unit and peril. Each factor increases the base premium.
            {adminUnitRows.length > 0 && (
              <span className="ml-2 text-sm">
                ({adminUnitRows.length} administrative unit{adminUnitRows.length !== 1 ? 's' : ''} configured)
              </span>
            )}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <div className="rounded-lg border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[250px] sticky left-0 bg-white z-10">Administrative Unit</TableHead>
                  {uniquePerils.map((peril) => (
                    <TableHead key={peril} className="text-center min-w-[120px]">
                      <Badge variant="secondary" className="text-xs">{peril}</Badge>
                    </TableHead>
                  ))}
                  {!readOnly && <TableHead className="w-[80px] text-center">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {adminUnitRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={uniquePerils.length + (readOnly ? 1 : 2)} className="text-center text-gray-500 py-8">
                      {readOnly 
                        ? "No loading factors configured."
                        : "No administrative units configured. Click \"Add Administrative Unit\" to start."
                      }
                    </TableCell>
                  </TableRow>
                ) : (
                  adminUnitRows.map((adminUnit, index) => (
                    <TableRow key={adminUnit.adminUnitId}>
                      <TableCell className="sticky left-0 bg-white z-10">
                        <AdminUnitHierarchyDisplay 
                          adminCode={adminUnit.adminUnitCode}
                          fallbackName={adminUnit.adminUnitName}
                        />
                      </TableCell>
                      {uniquePerils.map((peril, colIndex) => {
                        const cellKey = `${index}-${colIndex}`;
                        return (
                          <TableCell key={`${adminUnit.adminUnitId}-${peril}`} className="text-center p-2">
                            <div className="flex justify-center">
                              <EditableCell
                                ref={(el) => {
                                  editableCellRefs.current[cellKey] = el;
                                }}
                                value={adminUnit.factorsByPeril[peril] * 100} // Convert to percentage for display
                                onChange={(newValue) => 
                                  handleUpdateLoadingFactor(index, peril, newValue)
                                }
                                type="number"
                                min={0}
                                max={1000}
                                step={0.1}
                                suffix="%"
                                className="w-20 text-center"
                                disabled={readOnly}
                                onNavigate={(direction) => handleCellNavigation(index, colIndex, direction)}
                              />
                            </div>
                          </TableCell>
                        );
                      })}
                      {!readOnly && (
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAdminUnit(index)}
                            className="text-red-600 hover:text-red-700"
                            title={`Remove ${adminUnit.adminUnitName}`}
                          >
                            <TrashIcon size={14} />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Info about empty perils */}
          {uniquePerils.length === 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-blue-700 text-sm">
                <strong>Note:</strong> No perils are configured. Please configure perils in the Coverage Configuration section first to see loading factor columns.
              </div>
            </div>
          )}

          {/* Error Display */}
          {errors.length > 0 && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="text-red-700">
                <div className="font-medium mb-2">Loading Factor Validation Errors:</div>
                <ul className="text-sm space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>• {error.message}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AddAdminUnitDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onAdd={handleAddAdminUnit}
        existingFactors={loadingFactors}
      />
    </>
  );
}
